/** The (x, y, t) volume: two 3D textures (video + stain) plus the frame-to-slice plumbing. */

import { createTexture3D, type GLContext } from './gl';

export interface VolumeDims {
  width: number;
  height: number;
  layers: number;
}

export class Volume {
  private gl: WebGL2RenderingContext;
  width = 0;
  height = 0;
  layers = 0;

  videoTexture: WebGLTexture | null = null;
  stainTexture: WebGLTexture | null = null;

  /** Physical slice holding logical layer 0. */
  ringOffset = 0;
  /** Number of layers that hold real frames. */
  count = 0;

  /** Frame timestamps (seconds in the source clip) per logical layer. */
  private timestamps: number[] = [];

  private readonly capture: HTMLCanvasElement;
  private readonly captureCtx: CanvasRenderingContext2D;
  private readonly scratch: HTMLCanvasElement;
  private readonly scratchCtx: CanvasRenderingContext2D;
  private clearFbo: WebGLFramebuffer | null = null;

  constructor(private readonly ctx: GLContext) {
    this.gl = ctx.gl;

    const capture = document.createElement('canvas');
    const captureCtx = capture.getContext('2d', { alpha: false, willReadFrequently: false });
    if (!captureCtx) throw new Error('Could not create the 2D capture context');
    this.capture = capture;
    this.captureCtx = captureCtx;

    const scratch = document.createElement('canvas');
    const scratchCtx = scratch.getContext('2d', { alpha: false });
    if (!scratchCtx) throw new Error('Could not create the 2D scratch context');
    this.scratch = scratch;
    this.scratchCtx = scratchCtx;
  }

  get dims(): VolumeDims {
    return { width: this.width, height: this.height, layers: this.layers };
  }

  get byteSize(): number {
    return this.width * this.height * this.layers * 4 * 2;
  }

  get isEmpty(): boolean {
    return this.count === 0;
  }

  /** (Re)allocates both volumes. Any existing content is discarded. */
  allocate(dims: VolumeDims): void {
    const { gl, caps } = this.ctx;
    const max = caps.max3DTextureSize;
    const width = Math.max(2, Math.min(dims.width | 0, max));
    const height = Math.max(2, Math.min(dims.height | 0, max));
    const layers = Math.max(2, Math.min(dims.layers | 0, max));

    this.dispose();

    this.width = width;
    this.height = height;
    this.layers = layers;

    this.videoTexture = createTexture3D(gl, { width, height, depth: layers, filter: gl.LINEAR });
    this.stainTexture = createTexture3D(gl, { width, height, depth: layers, filter: gl.LINEAR });

    this.capture.width = width;
    this.capture.height = height;
    this.captureCtx.imageSmoothingEnabled = true;
    this.captureCtx.imageSmoothingQuality = 'high';
    this.scratchCtx.imageSmoothingEnabled = true;
    this.scratchCtx.imageSmoothingQuality = 'high';

    const fbo = gl.createFramebuffer();
    if (!fbo) throw new Error('createFramebuffer failed');
    this.clearFbo = fbo;

    this.clear();
  }

  /** Zeroes both volumes and resets the ring. */
  clear(): void {
    const { gl } = this;
    if (!this.videoTexture || !this.stainTexture || !this.clearFbo) return;

    gl.bindFramebuffer(gl.FRAMEBUFFER, this.clearFbo);
    gl.clearColor(0, 0, 0, 0);
    for (const tex of [this.videoTexture, this.stainTexture]) {
      for (let z = 0; z < this.layers; z++) {
        gl.framebufferTextureLayer(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT0, tex, 0, z);
        gl.clear(gl.COLOR_BUFFER_BIT);
      }
    }
    gl.framebufferTextureLayer(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT0, null, 0, 0);
    gl.bindFramebuffer(gl.FRAMEBUFFER, null);

    this.ringOffset = 0;
    this.count = 0;
    this.timestamps = [];
  }

  logicalToPhysical(layer: number): number {
    if (this.layers === 0) return 0;
    const l = ((layer % this.layers) + this.layers) % this.layers;
    return (this.ringOffset + l) % this.layers;
  }

  /**
   * Downsamples a frame into the capture canvas. Halves repeatedly first so large
   * frames get a proper box filter instead of point-sampled aliasing.
   * The image is flipped vertically because WebGL has no flipY for 3D textures.
   */
  prepareFrame(source: HTMLVideoElement | HTMLCanvasElement | HTMLImageElement | ImageBitmap): HTMLCanvasElement {
    const srcW =
      source instanceof HTMLVideoElement ? source.videoWidth : (source as HTMLCanvasElement | ImageBitmap).width;
    const srcH =
      source instanceof HTMLVideoElement ? source.videoHeight : (source as HTMLCanvasElement | ImageBitmap).height;

    if (!srcW || !srcH) return this.capture;

    let current: CanvasImageSource = source;
    let curW = srcW;
    let curH = srcH;

    while (curW >= this.width * 2 && curH >= this.height * 2) {
      const nextW = Math.max(this.width, Math.floor(curW / 2));
      const nextH = Math.max(this.height, Math.floor(curH / 2));
      // Reuse the scratch canvas in place; drawing a canvas onto itself is safe
      // here because we always shrink into the top-left corner.
      if (current !== this.scratch) {
        this.scratch.width = nextW;
        this.scratch.height = nextH;
        this.scratchCtx.imageSmoothingEnabled = true;
        this.scratchCtx.imageSmoothingQuality = 'high';
        this.scratchCtx.drawImage(current, 0, 0, curW, curH, 0, 0, nextW, nextH);
      } else {
        this.scratchCtx.drawImage(this.scratch, 0, 0, curW, curH, 0, 0, nextW, nextH);
      }
      current = this.scratch;
      curW = nextW;
      curH = nextH;
    }

    this.captureCtx.save();
    this.captureCtx.setTransform(1, 0, 0, -1, 0, this.height);
    this.captureCtx.drawImage(current, 0, 0, curW, curH, 0, 0, this.width, this.height);
    this.captureCtx.restore();
    return this.capture;
  }

  /** Uploads pixel data into one physical slice of the video volume. */
  uploadSlice(physicalZ: number, source: TexImageSource): void {
    const { gl } = this;
    if (!this.videoTexture) return;
    gl.bindTexture(gl.TEXTURE_3D, this.videoTexture);
    gl.pixelStorei(gl.UNPACK_ALIGNMENT, 4);
    gl.texSubImage3D(
      gl.TEXTURE_3D,
      0,
      0,
      0,
      physicalZ,
      this.width,
      this.height,
      1,
      gl.RGBA,
      gl.UNSIGNED_BYTE,
      source,
    );
    gl.bindTexture(gl.TEXTURE_3D, null);
  }

  /**
   * Appends a frame as the newest layer. Once the volume is full this recycles the
   * oldest slice, so `push` also drives the rolling live mode.
   * Returns the logical layer index the frame landed on.
   */
  push(source: HTMLVideoElement | HTMLCanvasElement | HTMLImageElement | ImageBitmap, timestamp = 0): number {
    const prepared = this.prepareFrame(source);
    let physical: number;
    let logical: number;

    if (this.count < this.layers) {
      physical = (this.ringOffset + this.count) % this.layers;
      logical = this.count;
      this.count++;
      this.timestamps.push(timestamp);
    } else {
      physical = this.ringOffset;
      this.ringOffset = (this.ringOffset + 1) % this.layers;
      logical = this.layers - 1;
      this.timestamps.shift();
      this.timestamps.push(timestamp);
    }

    this.uploadSlice(physical, prepared);
    return logical;
  }

  timestampAt(layer: number): number {
    return this.timestamps[Math.max(0, Math.min(this.timestamps.length - 1, Math.round(layer)))] ?? 0;
  }

  get duration(): number {
    if (this.timestamps.length < 2) return 0;
    return this.timestamps[this.timestamps.length - 1] - this.timestamps[0];
  }

  dispose(): void {
    const { gl } = this;
    if (this.videoTexture) {
      gl.deleteTexture(this.videoTexture);
      this.videoTexture = null;
    }
    if (this.stainTexture) {
      gl.deleteTexture(this.stainTexture);
      this.stainTexture = null;
    }
    if (this.clearFbo) {
      gl.deleteFramebuffer(this.clearFbo);
      this.clearFbo = null;
    }
    this.count = 0;
    this.ringOffset = 0;
    this.timestamps = [];
  }
}
